import { useState } from 'react'
import { Button, Modal } from '@mantine/core'
import AddStockMovementModal from '@/components/service_cases/ui/AddStockMovementModal'
import {
    ChevronDown,
    ChevronUp,
    Mail01,
    Printer,
} from '@untitled-ui/icons-react'
import dayjs from '@/lib/dayjs'
import useUserLocation from '@/hooks/useUserLocation'

// Statuses with actions fully enabled in production
const enabledStatuses = new Set([
    'waiting_acceptation',
    'customer_contacted',
    'in_store_dropoff',
    'waiting_shipment',
    'received',
    'watchmaker_diagnosis',
    'quote_to_prepare',
    'quote_sent',
    'quote_accepted',
    'quote_refused',
    'repairing',
    'quality_control',
    'to_ship',
    'to_store',
    'with_courier',
    'ready_in_store',
    'restocking',
])

const actionConfigs = {
    waiting_acceptation: {
        title: 'Accepter ou refuser le retour',
        waitingLabel: 'En attente de réponse',
        buttons: [
            {
                label: 'Marquer comme terminé',
                description: 'Le retour a déjà été traité.',
                consequences: 'Le statut passera à « Terminé ».',
                action: 'finish',
                variant: 'default',
            },
            {
                label: 'Marquer comme contacté',
                description: 'Le client a été contacté, en attente de confirmation.',
                consequences: 'Le statut passera à « Client contacté ».',
                action: 'mark_customer_contacted',
                variant: 'default',
            },
            {
                label: 'Accepter la demande',
                description:
                    'Le produit doit être envoyé pour prise en charge.',
                consequences:
                    'Un bordereau DHL sera généré et un email sera envoyé au client avec le bon de retour.',
                action: 'accept',
                withEmail: true,
            },
            {
                label: 'Déposé en boutique',
                description: 'Le client déposera la montre en boutique.',
                consequences: 'Le statut passera à « Déposé en boutique ». Aucun bordereau DHL ne sera généré.',
                action: 'accept_in_store',
            },
        ],
    },
    customer_contacted: {
        title: 'Accepter ou refuser le retour',
        waitingLabel: 'Client contacté, en attente de réponse',
        buttons: [
            {
                label: 'Marquer comme terminé',
                description: 'Le retour a déjà été traité.',
                consequences: 'Le statut passera à « Terminé ».',
                action: 'finish',
                variant: 'default',
            },
            {
                label: 'Accepter la demande',
                description:
                    'Le produit doit être envoyé pour prise en charge.',
                consequences:
                    'Un bordereau DHL sera généré et un email sera envoyé au client avec le bon de retour.',
                action: 'accept',
                withEmail: true,
            },
            {
                label: 'Déposé en boutique',
                description: 'Le client déposera la montre en boutique.',
                consequences: 'Le statut passera à « Déposé en boutique ». Aucun bordereau DHL ne sera généré.',
                action: 'accept_in_store',
            },
        ],
    },
    in_store_dropoff: {
        title: 'Réception en boutique',
        waitingLabel: 'En attente de dépôt par le client',
        buttons: [
            {
                label: 'Marquer comme reçu',
                description: 'Le client a déposé le produit en boutique.',
                consequences: 'Le statut passera à « Reçu ».',
                action: 'mark_received_from_store',
                withEmail: true,
            },
        ],
    },
    waiting_shipment: {
        title: 'Accuser réception du retour',
        waitingLabel: 'En attente de réception',
        buttons: [
            {
                label: 'Marquer comme reçu',
                description: 'Le produit du client a été réceptionné.',
                consequences: 'Le statut passera à « Reçu ».',
                action: 'receive',
                withEmail: true,
            },
        ],
    },
    received: {
        title: 'Démarrer le diagnostic',
        waitingLabel: 'En attente de diagnostic',
        buttons: [
            {
                label: 'Démarrer le diagnostic',
                description: 'Le produit est prêt à être diagnostiqué.',
                consequences: 'Le statut passera à « Diagnostic horloger ».',
                action: 'start_processing',
            },
        ],
    },
    watchmaker_diagnosis: {
        title: 'Diagnostic horloger',
        waitingLabel: 'En attente de diagnostic',
        buttons: [
            {
                label: 'Nécessite un devis',
                description:
                    'La montre est hors garantie ou nécessite des réparations non couvertes.',
                consequences: 'Le statut passera à « Devis à préparer ».',
                action: 'complete_diagnosis',
                actionData: { target: 'quote' },
                variant: 'default',
            },
            {
                label: 'Passer en réparation',
                description: 'La montre est sous garantie ou OK.',
                consequences: 'Le statut passera à « En réparation ».',
                action: 'complete_diagnosis',
                actionData: { target: 'repair' },
            },
            {
                label: 'Passer en contrôle qualité',
                description: 'La montre ne nécessite pas de réparation.',
                consequences: 'Le statut passera à « En contrôle qualité ».',
                action: 'complete_diagnosis',
                actionData: { target: 'quality_control' },
            },
        ],
    },
    quote_to_prepare: {
        title: 'Devis à préparer',
        waitingLabel: 'En attente de préparation du devis',
        buttons: [
            {
                label: 'Marquer le devis comme envoyé',
                description: 'Le devis a été communiqué au client.',
                consequences: 'Le statut passera à « Devis envoyé ».',
                action: 'mark_quote_sent',
            },
        ],
    },
    quote_sent: {
        title: 'Devis envoyé',
        waitingLabel: 'En attente de paiement',
        buttons: [
            {
                label: 'Devis refusé',
                description: 'Le client a refusé le devis.',
                consequences: 'Le statut passera à « Devis refusé ».',
                action: 'refuse_quote',
                variant: 'default',
            },
            {
                label: 'Devis payé',
                description: 'Le client a payé le devis.',
                consequences: 'Le statut passera à « Devis accepté ».',
                action: 'pay_quote',
            },
        ],
    },
    quote_accepted: {
        title: 'Devis payé, à passer en réparation',
        waitingLabel: 'En attente de réparation',
        buttons: [
            {
                label: 'Passer en réparation',
                description:
                    'Le devis a été payé, la réparation peut commencer.',
                consequences: 'Le statut passera à « En réparation ».',
                action: 'start_repair',
            },
        ],
    },
    quote_refused: {
        title: 'Devis refusé',
        waitingLabel: 'En attente de renvoi',
        buttons: [
            {
                label: 'Préparer le renvoi',
                description: 'Placer la montre pour renvoi au client.',
                consequences: 'Le statut passera à « Renvoi au client ».',
                action: 'prepare_return',
            },
        ],
    },
    repairing: {
        title: (sc) => `Réparer ${sc.product_name || ''}`,
        waitingLabel: 'En attente de réparation',
        buttons: [
            {
                label: 'Passer en contrôle qualité',
                description: 'La réparation est terminée.',
                consequences: 'Le statut passera à « En contrôle qualité ».',
                action: 'complete_repair',
                actionData: { target: 'quality_control' },
            },
        ],
    },
    quality_control: {
        title: 'Contrôle qualité',
        waitingLabel: 'En attente de contrôle qualité',
        buttons: [
            {
                label: 'Repasser en réparation',
                description: 'Le contrôle qualité a révélé un problème.',
                consequences: 'Le statut repassera à « En réparation ».',
                action: 'complete_quality_control',
                actionData: { target: 'repairing' },
                variant: 'default',
            },
            {
                label: 'Préparer le renvoi client',
                description: 'La montre est conforme, prête à renvoyer via DHL.',
                consequences: 'Le statut passera à « Renvoi au client ».',
                action: 'complete_quality_control',
                actionData: { target: 'to_ship' },
                showIf: (sc) => !sc.is_pickup_in_store,
            },
            {
                label: 'Préparer le renvoi en boutique',
                description: 'La montre est conforme, à renvoyer à la boutique pour récupération.',
                consequences: 'Le statut passera à « Renvoi à la boutique ».',
                action: 'complete_quality_control',
                actionData: { target: 'to_store' },
                showIf: (sc) => !!sc.is_pickup_in_store,
            },
            {
                label: 'Préparer la remise en stock',
                description:
                    'La montre est conforme, à remettre en stock (échange/remboursement).',
                consequences: 'Le statut passera à « Remise en stock ».',
                action: 'complete_quality_control',
                actionData: { target: 'restocking' },
                showIf: (sc) => ['return', 'swap_same'].includes(sc.type),
            },
        ],
    },
    to_ship: {
        title: 'Expédier la montre',
        waitingLabel: "En attente d'expédition",
        buttons: [
            {
                label: 'Imprimer et marquer comme expédié',
                description: 'Le produit est prêt à être expédié.',
                consequences:
                    'Un bordereau DHL sera généré, un email sera envoyé au client et le statut passera à « Terminé ».',
                action: 'print_and_send',
                withEmail: true,
            },
        ],
    },
    to_store: {
        title: 'Renvoi à la boutique',
        waitingLabel: 'En attente de remise au coursier',
        buttons: [
            {
                label: 'Confier au coursier',
                description: 'La montre a été remise au coursier pour livraison en boutique.',
                consequences: 'Le statut passera à « Avec le coursier ».',
                action: 'hand_to_courier',
            },
        ],
    },
    with_courier: {
        title: 'Renvoi à la boutique',
        waitingLabel: 'En livraison à la boutique',
        buttons: [
            {
                label: 'Marquer comme arrivé en boutique',
                description: 'La montre est arrivée en boutique, prête à être récupérée.',
                consequences: 'Le statut passera à « Prêt en boutique ».',
                action: 'mark_arrived_in_store',
            },
        ],
    },
    ready_in_store: {
        title: 'Le client récupérera la montre en boutique',
        waitingLabel: 'En attente de récupération par le client',
        buttons: [
            {
                label: 'Confirmer la récupération',
                description: 'Le client est venu récupérer sa montre en boutique.',
                consequences: 'Le statut passera à « Terminé ».',
                action: 'confirm_pickup',
            },
        ],
    },
    restocking: {
        title: 'Remise en stock',
        waitingLabel: 'En attente de remise en stock',
        buttons: [
            {
                label: 'Confirmer la remise en stock',
                description: 'La montre a été remise en stock.',
                consequences: 'Le statut passera à « Terminé ».',
                action: 'confirm_restock',
            },
        ],
    },
}

export default function ServiceCaseActions({
    serviceCase,
    performAction,
    onPrintLabel = null,
    onMutate = null,
}) {
    const [loadingAction, setLoadingAction] = useState(null)
    const [printingLabel, setPrintingLabel] = useState(false)
    const [confirmingBtn, setConfirmingBtn] = useState(null)
    const [confirmModalOpened, setConfirmModalOpened] = useState(false)
    const [sendEmail, setSendEmail] = useState(true)
    const [emailPreviewOpened, setEmailPreviewOpened] = useState(false)
    const [addStockModalOpened, setAddStockModalOpened] = useState(false)

    const { userPrinters } = useUserLocation()
    const shippingLabelPrinter = userPrinters.shipping_label

    const config = actionConfigs[serviceCase.status]
    const isEnabled = enabledStatuses.has(serviceCase.status)
    const canSendEmail = serviceCase.can_send_email

    const handleConfirm = async () => {
        if (!confirmingBtn) return
        setLoadingAction(confirmingBtn.index)
        try {
            const email =
                confirmingBtn.withEmail && canSendEmail && sendEmail
                    ? { send: true }
                    : null
            const actionData = { ...(confirmingBtn.actionData || {}) }
            if (confirmingBtn.action === 'print_and_send' && shippingLabelPrinter) {
                actionData.printnode_ids = shippingLabelPrinter.printnode_ids
            }
            await performAction(
                confirmingBtn.action,
                actionData,
                email
            )
        } catch (e) {
            // Error handled by useServiceCase
        } finally {
            setLoadingAction(null)
            setConfirmModalOpened(false)
        }
    }

    const handlePrintLabel = async () => {
        setPrintingLabel(true)
        try {
            await onPrintLabel()
        } finally {
            setPrintingLabel(false)
        }
    }

    const { classes } = serviceCase.status_config || {}

    const title = config
        ? typeof config.title === 'function'
            ? config.title(serviceCase)
            : config.title
        : null

    const waitingSince =
        config && serviceCase.current_status_at
            ? dayjs().to(dayjs(serviceCase.current_status_at), true)
            : null

    return (
        <>
            {config && (
                <div
                    className={`${classes.bg} border ${classes.border} rounded-xl p-5 flex flex-col gap-4`}
                >
                    <div className="flex flex-col items-center gap-1">
                        <span className="font-medium text-lg">{title}</span>
                        {waitingSince && (
                            <span className={`text-sm ${classes.text}`}>
                                {config.waitingLabel} depuis {waitingSince}
                            </span>
                        )}
                    </div>

                    <div className="flex flex-col items-center gap-3">
                        <div className="flex flex-wrap justify-center gap-3">
                            {config.buttons.filter(btn => !btn.showIf || btn.showIf(serviceCase)).map((btn, i) => (
                                <Button
                                    key={btn.label}
                                    variant={btn.variant || 'filled'}
                                    loading={isEnabled && loadingAction === i}
                                    disabled={
                                        !isEnabled ||
                                        (loadingAction != null &&
                                            loadingAction !== i)
                                    }
                                    onClick={() => {
                                        setSendEmail(true)
                                        setEmailPreviewOpened(false)
                                        setConfirmingBtn({ ...btn, index: i })
                                        setConfirmModalOpened(true)
                                    }}
                                >
                                    {btn.label}
                                </Button>
                            ))}
                            {serviceCase.status === 'restocking' && (
                                <Button
                                    variant="default"
                                    disabled={!isEnabled}
                                    onClick={() => setAddStockModalOpened(true)}
                                >
                                    Enregistrer un mouvement de stock
                                </Button>
                            )}
                            {serviceCase.status === 'received' &&
                                onPrintLabel && (
                                    <Button
                                        variant="default"
                                        loading={printingLabel}
                                        disabled={!isEnabled || loadingAction}
                                        leftSection={
                                            <Printer width={16} height={16} />
                                        }
                                        onClick={handlePrintLabel}
                                    >
                                        Imprimer l'étiquette SAV
                                    </Button>
                                )}
                        </div>
                        {!isEnabled && (
                            <span className="text-xs text-gray-400">
                                Bientôt disponible
                            </span>
                        )}
                    </div>
                </div>
            )}

            <AddStockMovementModal
                serviceCase={serviceCase}
                opened={addStockModalOpened}
                onClose={() => setAddStockModalOpened(false)}
                onSuccess={onMutate}
            />

            <Modal
                opened={confirmModalOpened}
                onClose={() => setConfirmModalOpened(false)}
                title={confirmingBtn?.label}
                centered
            >
                <div className="flex flex-col gap-4">
                    {confirmingBtn?.description && (
                        <p className="text-sm text-gray-500">
                            {confirmingBtn.description}
                        </p>
                    )}

                    {confirmingBtn?.consequences && (
                        <p className="text-sm text-gray-400">
                            {typeof confirmingBtn.consequences === 'function'
                                ? confirmingBtn.consequences(serviceCase)
                                : confirmingBtn.consequences}
                        </p>
                    )}

                    {confirmingBtn?.withEmail && (
                        <div className="border rounded-lg flex flex-col overflow-hidden">
                            {!canSendEmail ? (
                                <div className="flex items-center gap-2 text-sm text-gray-400 p-4">
                                    <Mail01 width={14} height={14} />
                                    <span>
                                        Aucun email ne sera envoyé (email absent
                                        ou notifications désactivées)
                                    </span>
                                </div>
                            ) : (
                                <>
                                    <button
                                        type="button"
                                        className="flex items-center justify-between p-4 text-sm hover:bg-gray-50 transition-colors"
                                        onClick={() => setSendEmail(!sendEmail)}
                                    >
                                        <span className="flex items-center gap-2">
                                            <Mail01
                                                width={14}
                                                height={14}
                                                className="text-gray-400 shrink-0"
                                            />
                                            Envoyer un email au client
                                        </span>
                                        <span
                                            className={`text-xs font-medium px-2 py-0.5 rounded-sm ${sendEmail ? 'bg-blue-50 text-blue-600' : 'bg-gray-100 text-gray-400'}`}
                                        >
                                            {sendEmail ? 'Activé' : 'Désactivé'}
                                        </span>
                                    </button>

                                    {sendEmail &&
                                        serviceCase.ticket_default_response && (
                                            <div className="border-t">
                                                <button
                                                    type="button"
                                                    className="flex items-center justify-between w-full p-4 text-xs text-gray-400 hover:bg-gray-50 transition-colors"
                                                    onClick={() =>
                                                        setEmailPreviewOpened(
                                                            !emailPreviewOpened
                                                        )
                                                    }
                                                >
                                                    <span>
                                                        Aperçu de l'email
                                                    </span>
                                                    {emailPreviewOpened ? (
                                                        <ChevronUp
                                                            width={14}
                                                            height={14}
                                                        />
                                                    ) : (
                                                        <ChevronDown
                                                            width={14}
                                                            height={14}
                                                        />
                                                    )}
                                                </button>
                                                {emailPreviewOpened && (
                                                    <div
                                                        className="prose prose-sm max-w-none [&_a]:text-blue-600 [&_a]:underline whitespace-pre-line p-4"
                                                        dangerouslySetInnerHTML={{
                                                            __html: serviceCase.ticket_default_response,
                                                        }}
                                                    />
                                                )}
                                            </div>
                                        )}
                                </>
                            )}
                        </div>
                    )}

                    <Button
                        fullWidth
                        loading={loadingAction != null}
                        onClick={handleConfirm}
                    >
                        Confirmer
                    </Button>
                </div>
            </Modal>
        </>
    )
}
